import type { EnrichedProductField } from '../types/run-content.api.types';
import type { FieldSets } from '../types/run-detail.api.types';

import { fieldKey, isGenerated } from './enriched-fields';

/**
 * One field code in upper snake — the join key between a product row's field names
 * (content-generation's snake_case `product_highlight`) and the run's field-set codes
 * (catalog-service's `PRODUCT_HIGHLIGHT`). Built on `fieldKey`, so camelCase folds the same way.
 */
export function fieldCode(name: string): string {
  return fieldKey(name).toUpperCase();
}

/** The singular form of a code — `PRODUCT_HIGHLIGHTS` and `PRODUCT_HIGHLIGHT` name one field. */
function singular(code: string): string {
  return code.endsWith('S') ? code.slice(0, -1) : code;
}

/** Every field any list names, in requested → delivered → enriched order, deduplicated. */
export function fieldSetFields(data: FieldSets): string[] {
  return Array.from(new Set([...data.requested, ...data.delivered, ...(data.enriched ?? [])]));
}

/** What the batch planned for one field, independent of any one product. */
export interface FieldPlanEntry {
  /** On `requested` — the operator picked it when setting up the batch. */
  asked: boolean;
  /** On `delivered` — the list delivery sends to this destination. */
  willDeliver: boolean;
  /** On `alwaysOn` — content generation writes it for this destination whatever was picked. */
  alwaysOn: boolean;
}

/** The batch's field plan, keyed by normalised code so any field-name shape can look itself up. */
export interface FieldPlan {
  /** The raw field-set codes, as the matrix lists them. */
  requested: string[];
  delivered: string[];
  /** Plan for one field name (any shape); a name on no list gets all-false. */
  entryOf: (name: string) => FieldPlanEntry;
}

/** A lookup set over normalised + singular codes, so `has` tolerates case, separators and plurals. */
function codeSet(codes: readonly string[] | null | undefined): (name: string) => boolean {
  const set = new Set((codes ?? []).map((c) => singular(fieldCode(c))));
  return (name) => set.has(singular(fieldCode(name)));
}

/** Builds the field plan from the run's field sets — the single place codes are normalised. */
export function buildFieldPlan(data: FieldSets): FieldPlan {
  const asked = codeSet(data.requested);
  const willDeliver = codeSet(data.delivered);
  const alwaysOn = codeSet(data.alwaysOn);
  return {
    requested: data.requested,
    delivered: data.delivered,
    entryOf: (name) => ({
      asked: asked(name),
      willDeliver: willDeliver(name),
      alwaysOn: alwaysOn(name),
    }),
  };
}

/** One product's plan-vs-reality roll-up — the numbers the field-plan strip shows. */
export interface ProductFieldPlanSummary {
  askedCount: number;
  deliverCount: number;
  /** Fields content generation wrote a value for, for THIS product. */
  writtenCount: number;
  /** Asked-for codes this product got no generated value for (never hidden in the generic list). */
  askedNotWritten: string[];
  /** True when THIS product got a generated value for the code (any name shape). */
  writtenForProduct: (code: string) => boolean;
}

/** Joins one product's fields onto the batch plan: what was written vs what was asked for. */
export function summarizeProductFieldPlan(
  fields: readonly EnrichedProductField[],
  plan: FieldPlan,
): ProductFieldPlanSummary {
  const written = new Set(
    fields.filter((f) => isGenerated(f)).map((f) => singular(fieldCode(f.name))),
  );
  const writtenForProduct = (code: string) => written.has(singular(fieldCode(code)));
  return {
    askedCount: plan.requested.length,
    deliverCount: plan.delivered.length,
    writtenCount: written.size,
    askedNotWritten: plan.requested.filter((code) => !writtenForProduct(code)),
    writtenForProduct,
  };
}
